import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Footer from '../Footer/Footer';
import HeaderNotification from '../HeaderNotification/HeaderNotification';
import PushNotificationModal from '../PushNotificationModal/PushNotificationModal';
import styles from './Layout.module.css';

function Layout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [userName, setUserName] = useState('Admin');
    const [userAvatar, setUserAvatar] = useState(() => {
        try {
            return localStorage.getItem('avatar') || '';
        } catch (e) {
            return '';
        }
    });

    // The profile page broadcasts name/avatar changes so the header updates at once
    useEffect(() => {
        const onProfile = (e) => {
            const d = (e && e.detail) || {};
            if (d.name) setUserName(d.name);
            setUserAvatar(d.avatar || '');
        };
        window.addEventListener('ej-profile-updated', onProfile);
        return () =>
            window.removeEventListener('ej-profile-updated', onProfile);
    }, []);
    const location = useLocation();
    const navigate = useNavigate();

    // Close sidebar on route change for mobile
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Get username from localStorage
    useEffect(() => {
        const storedUserName = localStorage.getItem('username');
        if (storedUserName) {
            setUserName(storedUserName);
        }
    }, []);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleUserInfoClick = () => {
        navigate('/profile');
    };

    return (
        <div className={styles.layout}>
            {/* Sidebar */}
            <div
                className={`${styles.sidebarContainer} ${
                    sidebarOpen ? styles.sidebarOpen : ''
                }`}
            >
                <Sidebar />
            </div>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <div className={styles.mainContent}>
                {/* Desktop Header */}
                <div className={styles.desktopHeader}>
                    <div className={styles.headerLeft}>
                        <h1 className={styles.pageTitle}>
                            {getPageTitle(location.pathname)}
                        </h1>
                    </div>
                    <div className={styles.headerRight}>
                        <div className={styles.headerActions}>
                            <HeaderNotification />
                        </div>
                        <div
                            className={styles.userInfo}
                            onClick={handleUserInfoClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleUserInfoClick();
                                }
                            }}
                            title="Go to profile settings"
                        >
                            <span className={styles.userName}>{userName}</span>
                            <div className={styles.userAvatar}>
                                {userAvatar ? (
                                    <img
                                        src={userAvatar}
                                        alt=""
                                        className={styles.userAvatarImg}
                                        onError={() => setUserAvatar('')}
                                    />
                                ) : (
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile Header */}
                <div className={styles.mobileHeader}>
                    <div className={styles.mobileHeaderLeft}>
                        <button
                            className={styles.menuButton}
                            onClick={toggleSidebar}
                            aria-label="Toggle menu"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path d="M2 4h16v2H2V4zm0 5h16v2H2V9zm0 5h16v2H2v-2z" />
                            </svg>
                        </button>
                        <h1 className={styles.mobileTitle}>
                            {getPageTitle(location.pathname)}
                        </h1>
                    </div>
                    <div className={styles.mobileHeaderRight}>
                        <HeaderNotification />
                        <div
                            className={styles.mobileUserInfo}
                            onClick={handleUserInfoClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleUserInfoClick();
                                }
                            }}
                            title="Go to profile settings"
                        >
                            <div className={styles.mobileUserAvatar}>
                                {userAvatar ? (
                                    <img
                                        src={userAvatar}
                                        alt=""
                                        className={styles.userAvatarImg}
                                        onError={() => setUserAvatar('')}
                                    />
                                ) : (
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className={styles.content}>{children}</div>

                {/* Footer */}
                <Footer />
            </div>

            {/* Push Notification Permission Modal */}
            <PushNotificationModal />
        </div>
    );
}

// Helper function to get page title based on route
function getPageTitle(pathname) {
    const titles = {
        '/dashboard': 'Dashboard',
        '/product': 'Products',
        '/add-product': 'Add Product',
        '/category': 'Categories',
        '/brand': 'Brands',
        '/special-category': 'Home Categories',
        '/order-list': 'Orders',
        '/users': 'Customers',
        '/advance-report': 'Analytics',
        '/customer-analytics': 'Customer Analytics',
        '/expense': 'Expense',
        '/accounts': 'Accounts',
        '/purchase': 'Purchase',
        '/visitor-video': 'Visitor Video',
        '/contact-messages': 'Contact Messages',
        '/business-email': 'Business Email',
        '/buy-domain': 'Buy Domain',
        '/banner-list': 'Banners',
        '/landing-list': 'Landing Pages',
        '/new-store': 'Store Settings',
        '/dns-list': 'DNS Settings',
        '/theme': 'Theme Colors',
        '/team': 'Team Management',
        '/billing': 'Billing',
        '/profile': 'Profile',
        '/settings': 'Settings',
        '/policy': 'Policy',
        // Super Admin Routes
        '/superadmin': 'Platform Overview',
        '/superadmin/stores': 'Stores',
        '/superadmin/users': 'Users',
        '/superadmin/packages': 'Packages',
        '/superadmin/billing': 'Billing',
        '/superadmin/analytics': 'Analytics',
        '/superadmin/settings': 'Settings',
    };

    return titles[pathname] || 'Dashboard';
}

export default Layout;
